export type ProductStatus =
  | 'active_buybox_win'
  | 'active_pressure_winner'
  | 'deactivated'
  | 'not_activated';

// Filter types include 'active' which combines buybox + pressure winners
export type FilterStatus = ProductStatus | 'active';

export interface Product {
  id: string;
  status: ProductStatus;
  deactivationReason?: string;
  [key: string]: unknown; // Flexible fields
}

export interface User {
  id: string;
  email: string;
  name: string;
  membershipTier?: number;
  membershipValidTill?: string; // ISO date string
  isExpired?: boolean; // True if membership has expired
}

// Predefined API key version/type (legacy - kept for backwards compatibility)
export interface ApiKeyVersion {
  id: string;
  name: string;
  durationDays: number; // How many days the key is valid
}

// Legacy ApiKey interface (for components that use the old format)
export interface ApiKey {
  id: string;
  versionId: string; // Reference to ApiKeyVersion
  versionName: string; // Display name from version
  key: string;
  expiresAt: Date;
  daysRemaining: number;
  isActive: boolean;
  createdAt: Date;
}

// New API key interface from server (AuthKeys table)
export interface ApiKeyFromServer {
  id: string;
  platform: string;
  keyType: string;
  sellerId: string | null;
  expiresAt: string | null;
  isActive: boolean;
}

// Processed API key for display
export interface ProcessedApiKey {
  id: string;
  platform: string;
  platformDisplayName: string;
  keyType: string;
  sellerId: string | null;
  expiresAt: Date | null;
  daysRemaining: number;
  isActive: boolean;
}

export interface FilterConfig {
  id: FilterStatus;
  label: string;
  count: number;
}

export interface ColumnConfig<T = object> {
  key: keyof T | string;
  header: string;
  width?: string;
  className?: string;
  render?: (value: unknown, item: T) => React.ReactNode;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

// =============================================================================
// Befel (Order/Command) Types
// =============================================================================

// Emir Tipi (Order Type) - matches EmirTipleri enum in backend
export type EmirTipi = 0 | 1; // 0=BUYBOX_ELE_GECIR, 1=BASKI_UYGULA

// Befel from server (raw API response)
export interface BefelFromServer {
  id: string;
  platform: string;
  urun: string;
  sku: string;
  alt: number;
  ust: number;
  emirTipi: number;
  active: boolean;
  workerID: number;
  deaktivasyonNedeni: number | null;
  url: string | null;
  modelKodu: string | null;
  priceIncludesCommission: boolean;
  commissionPct: number | null;
}

// Processed Befel for display
export interface ProcessedBefel {
  id: string;
  platform: string;
  platformDisplayName: string;
  urun: string;
  sku: string;
  altFiyat: number;
  ustFiyat: number;
  paraTipi: CurrencyType;
  emirTipi: EmirTipi;
  emirTipiLabel: string;
  active: boolean;
  workerID: number;
  status: 'working' | 'waiting' | 'inactive';
  deaktivasyonNedeni: string | null;
  url: string | null;
  priceIncludesCommission: boolean;
  commissionPct: number | null;
  modelKodu: string | null;
  thumbnail: string | null;
  activateCoupon: boolean;
  activatePromotion: boolean;
}

// Filter status for Befels
export type BefelFilterStatus = 'all' | 'active' | 'working' | 'waiting' | 'inactive';

// Filter config for Befels (different from products)
export interface BefelFilterConfig {
  id: BefelFilterStatus;
  label: string;
  count: number;
}

// =============================================================================
// Platform Product Types (for Add Orders page)
// =============================================================================

// Currency types (matches ParaTipleri in backend)
export type CurrencyType = 0 | 1 | 2 | 3; // 0=TRY, 1=USD, 2=EUR, 3=CNY

export const CURRENCY_CODES: Record<CurrencyType, string> = {
  0: 'TRY',
  1: 'USD',
  2: 'EUR',
  3: 'CNY',
};

export const CURRENCY_SYMBOLS: Record<CurrencyType, string> = {
  0: '₺',
  1: '$',
  2: '€',
  3: '¥',
};

// Product from platform API (via BackendMaster)
export interface PlatformProductFromServer {
  id: string;
  platform: string;
  name: string;
  sku: string;
  barcode?: string;
  modelKodu?: string;
  currentPrice: number;
  currency: CurrencyType;
  stock: number;
  url?: string;
  attributes?: Record<string, string>;  // Key-value pairs from product attributes
  images?: string[];  // List of image URLs
  categoryName?: string;  // Product category
  brandName?: string;  // Product brand
  // Listing-level status flags (HB only)
  isSalable?: boolean | null;
  isLocked?: boolean | null;
  isSuspended?: boolean | null;
  isFrozen?: boolean | null;
}

// Processed platform product for display
export interface ProcessedPlatformProduct {
  id: string;
  platform: string;
  platformDisplayName: string;
  name: string;
  sku: string;
  barcode?: string;
  modelKodu?: string;
  currentPrice: number;
  currency: CurrencyType;
  currencyCode: string;
  currencySymbol: string;
  stock: number;
  url?: string;
  attributes?: Record<string, string>;  // Key-value pairs from product attributes
  images?: string[];  // List of image URLs
  categoryName?: string;  // Product category
  brandName?: string;  // Product brand
  // Listing-level status flags (HB only)
  isSalable?: boolean | null;
  isLocked?: boolean | null;
  isSuspended?: boolean | null;
  isFrozen?: boolean | null;
}

// =============================================================================
// Bulk Upload Types
// =============================================================================

// Single order item for bulk upload request
export interface BulkOrderItem {
  barkod: string;
  altFiyat: number;
  ustFiyat: number;
  paraBirimi: number;  // 0=TRY, 1=USD, 2=EUR, 3=CNY
  emirTipi: number;    // 0=BuyBox, 1=Pressure
  priceIncludesCommission: boolean;  // Whether prices include commission
  activateCoupon: boolean;  // Enable automatic coupon management
  activatePromotion: boolean;  // Enable automatic promotion management
}

// Bulk upload request
export interface BulkCreateRequest {
  platform: string;
  orders: BulkOrderItem[];
}

// Result for a single order in bulk upload response
export interface BulkResultItem {
  row: number;
  barkod: string;
  success: boolean;
  error?: string;
  befelId?: string;
  action?: 'CREATED' | 'UPDATED' | 'UNCHANGED';  // Whether order was created, updated, or unchanged
  note?: string;  // Additional note (e.g., price bounds warning in Turkish)
}

// Bulk upload response from server
export interface BulkCreateResponse {
  results: BulkResultItem[];
  successCount: number;
  errorCount: number;
  createdCount: number;
  updatedCount: number;
  unchangedCount: number;  // Count of items that matched existing befels exactly (no API call needed)
}

// Excel row structure (parsed from uploaded file)
export interface ExcelOrderRow {
  barkod: string;
  altFiyat: number | string;
  ustFiyat: number | string;
  paraBirimi?: number | string;
  emirTipi: number | string;
}

// Bulk upload error codes (for translation)
export type BulkUploadErrorCode =
  | 'MISSING_BARCODE'
  | 'INVALID_PRICE'
  | 'NEGATIVE_PRICE'
  | 'TIER_LIMIT_REACHED'
  | 'DUPLICATE_ORDER'
  | 'DUPLICATE_IN_BATCH'
  | 'PRODUCT_NOT_FOUND'
  | 'PRODUCT_NOT_ON_SALE'
  | 'PRODUCT_ARCHIVED'
  | 'PRICE_TOO_LOW'
  | 'PRICE_TOO_HIGH'
  | 'API_AUTH_FAILED'
  | 'API_ACCESS_DENIED'
  | 'API_ERROR'
  | 'API_CONNECTION_ERROR';
